<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { planReadOnlyReason, useProjectPlanWrite } from '@/utils/project-write';
import { eqsApi } from '@/api/eqs';
import { useSession } from '@/auth/session';
import ExportDataButton from '@/components/ExportDataButton.vue';
import GanttBoard from '@/components/GanttBoard.vue';
import PlanNodeDrawer from '@/components/PlanNodeDrawer.vue';
import NodeRescheduleDialog from '@/components/NodeRescheduleDialog.vue';
import SchedulePlanner from '@/components/SchedulePlanner.vue';
import PageState from '@/components/PageState.vue';
import { notify } from '@/services/notice';
import type { Milestone, PlanDependency, PlanTask, Project, ProjectPlanResponse } from '@/types/domain';
import { buildExportFilename } from '@/utils/csv-export';
import { planExportColumns, planTaskExportRow } from '@/utils/data-export';
import { errorMessage, formatDate, statusText } from '@/utils/format';
import { milestoneStage } from '@/utils/milestones';
import { confirmAction } from '@/services/confirm';
import { useRescheduleAsk } from '@/composables/useRescheduleAsk';
import { dayOf, nodeNeedsRescheduleReason, rescheduleFields } from '@/utils/plan-node-reschedule';
import { buildProgressIndex } from '@/utils/plan-progress';

// 工作区把项目详情同时当作 project 传下来，详情里带着里程碑（Project 类型没声明）
const props = defineProps<{ project: Project & { milestones?: Milestone[] } }>();
const route = useRoute();
const { access } = useSession();
const data = ref<ProjectPlanResponse | null>(null);
const loading = ref(true);
const error = ref('');
const savingNodeId = ref<string | number | null>(null);
const phaseFilter = ref('all');
/*
  改计划完成日要写原因（0926 Tony）。已排期、还开着的活动改了完成日才问；
  拖拽也问——手势是确认，但原因得有人写；顺延和撤销自动带原因，不再弹。
*/
const reschedule = useRescheduleAsk();

/*
  能不能改 = 账号不是只读 且 这个项目在自己名下。
  所有项目都能看（Tony 2026-08-18 定的口径），收着的只有写。
*/
/*
  0914 Tony 第 12 条：计划甘特只给项目经理（管理者）改。
  比项目的一般写权限窄一档——判据在 useProjectPlanWrite 一处，
  后端 guardPlanWrite 用的是同一条，界面不自己再判一遍。
*/
const canWrite = useProjectPlanWrite(computed(() => props.project));
const planReadOnly = computed(() =>
  planReadOnlyReason(props.project, access.value?.canWrite !== false));
/**
 * 「阶段工作内容」里把活动标成不适用后，甘特图上就不该再占一行。
 *
 * 两个页面读的是同一份计划节点（pm_plan_nodes），改一边另一边刷新即生效；
 * 差的只是甘特从来没认过「适用」这一列（试用反馈 0818）。
 *
 * 隐藏但**不静默**：横幅上写清隐藏了几条，一键可以显示回来——
 * 列表少了数据却不吭声，是这套系统吃过亏的老毛病。
 */
const rawTasks = computed(() => data.value?.tasks ?? []);
const showNotApplicable = ref(false);
const notApplicableCount = computed(
  () => rawTasks.value.filter((task) => task.applicable === false).length,
);
const allTasks = computed(() => (
  showNotApplicable.value
    ? rawTasks.value
    : rawTasks.value.filter((task) => task.applicable !== false)
));
const activityMap = computed(() => new Map(
  allTasks.value
    .filter((task) => task.activityId !== null && task.activityId !== undefined)
    .map((task) => [String(task.activityId), task]),
));
const phaseRoots = computed(() => allTasks.value.filter((task) =>
  (task.nodeType === 'phase' || /phase|stage|阶段/i.test(task.phaseCode))
  && (task.parentId === null || String(task.parentId) === '0')));
const phaseOptions = computed(() => phaseRoots.value.map((task) => ({
  value: String(task.activityId ?? task.id),
  label: task.name,
})));

function taskKey(task: PlanTask): string {
  return String(task.activityId ?? task.id);
}

function phaseFor(task: PlanTask): string | null {
  const seen = new Set<string>();
  let current: PlanTask | undefined = task;
  while (current) {
    if (
      (current.nodeType === 'phase' || /phase|stage|阶段/i.test(current.phaseCode))
      && (current.parentId === null || String(current.parentId) === '0')
    ) return taskKey(current);
    const parent = current.parentId === null ? '' : String(current.parentId);
    if (!parent || parent === '0' || seen.has(parent)) break;
    seen.add(parent);
    current = activityMap.value.get(parent);
  }
  return null;
}

const visibleTasks = computed(() => {
  const phaseKeys = new Set(phaseRoots.value.map((task) => taskKey(task)));
  let list = allTasks.value;
  if (phaseFilter.value !== 'all') list = list.filter((task) => phaseFor(task) === phaseFilter.value);
  // 阶段行留着，只滤任务——全滤掉的话甘特就没有分组标题了
  if (mineOnly.value) list = list.filter((task) => task.isMine === true || phaseKeys.has(taskKey(task)));
  return list;
});
/** 完成度和甘特条、阶段工作表同一个算法（utils/plan-progress），导出也用它 */
const progressIndex = computed(() => buildProgressIndex(rawTasks.value));
const exportRows = computed(() => visibleTasks.value.map((task) => {
  const phaseKey = phaseFor(task);
  const phaseName = phaseRoots.value.find((phase) => taskKey(phase) === phaseKey)?.name || '未归入阶段';
  return planTaskExportRow(props.project, task, phaseName, statusText(task.status),
    progressIndex.value.get(String(task.id))?.value ?? null);
}));
const exportFilename = computed(() =>
  buildExportFilename([props.project.projectNo, '甘特计划']));

/**
 * Gate 导航过去点了没反应。
 *
 * 每个项目页顶部那条 GATE1–GATE6 是最显眼的元素，链接指向 ?gate=GATEn，
 * 但全前端没有一处读这个参数——六个入口全是装饰。
 *
 * 这里把它接上：GATEn 对应 APQP 的 P0n 阶段（与后端 MILESTONE_DEFINITIONS
 * 的 stageCode 同一套口径），点进来过滤到该阶段，并给出这道 Gate 的就绪度。
 */
const gateCode = computed(() => {
  const raw = Array.isArray(route.query.gate) ? route.query.gate[0] : route.query.gate;
  const code = String(raw ?? '').trim().toUpperCase().replace(/[\s-]/gu, '_');
  // 认全部 14 个里程碑，不只是 GATE1-6：项目概览那张表对 I_OTS / OTS / PPAP
  // 也都链到 ?gate=，原来的 /^GATE[1-6]$/ 把它们统统判成无效——又是一批装饰链接。
  return milestoneStage(code) ? code : null;
});

/** 这道门落在哪个阶段：GATE3→P03，I_OTS→P04（见 utils/milestones 的 milestoneStage） */
const gateStage = computed(() => milestoneStage(gateCode.value));

/** P03 → 匹配名字以 P03 开头的阶段根节点 */
const gatePhase = computed(() => {
  const stage = gateStage.value;
  if (!stage) return null;
  return phaseRoots.value.find((task) => task.name.trim().toUpperCase().startsWith(stage)) ?? null;
});

/*
  ?mine=1：Gate 提醒里「你名下 N 项」点过来，直接落到自己那几项上。
  不是静默过滤——筛选栏上有一枚能关掉的按钮，并写明隐藏了多少条。
*/
const mineOnly = ref(false);
watch(() => route.query.mine, (value) => {
  mineOnly.value = (Array.isArray(value) ? value[0] : value) === '1';
}, { immediate: true });
const mineCount = computed(() => allTasks.value.filter((task) => task.isMine === true).length);

/**
 * 这道 Gate 的就绪度：关键交付物完成了多少、还有多少没排期。
 * 不做「能不能过 Gate」的判断——那是评审会的决定，系统只负责把事实摆出来。
 */
const gateReadiness = computed(() => {
  const phase = gatePhase.value;
  if (!phase) return null;
  const key = taskKey(phase);
  const inPhase = allTasks.value.filter((task) => phaseFor(task) === key);
  const critical = inPhase.filter((task) => task.keyDeliverable);
  // 需要审批的关键交付物，「做完」不等于「批准」——
  // 全库 1655 个关键交付物里 1190 个挂着审批流，把 completed 当成就绪
  // 等于让没过评审的东西替 Gate 背书。
  // actual_at 同样算完成：迁移过来的节点 status_key 全是空的，
  // 只认 status 的话 Gate 就绪度永远是 0（见任务看板里的同一处口径）
  const isDone = (task: PlanTask): boolean => (
    task.approvalRequired
      ? task.approvalStatus === 'approved' || task.status === 'approved'
      : task.status === 'completed' || task.status === 'approved' || Boolean(task.actualEnd)
  );
  const done = critical.filter(isDone);
  const awaitingApproval = critical.filter((task) =>
    task.approvalRequired && !isDone(task)
    && (task.status === 'completed' || task.approvalStatus === 'in_review'));
  return {
    phaseName: phase.name,
    total: inPhase.length,
    criticalTotal: critical.length,
    criticalDone: done.length,
    awaitingApproval: awaitingApproval.length,
    unscheduled: inPhase.filter((task) => !task.planStart || !task.planEnd).length,
    overdue: inPhase.filter((task) => task.overdue).length,
  };
});

const gateMilestone = computed(() => {
  if (!gateCode.value) return null;
  return (props.project.milestones ?? []).find((item) =>
    String(item.milestoneCode).toUpperCase().replace(/[\s_-]/gu, '') === gateCode.value) ?? null;
});

const focusTaskId = computed(() => {
  const raw = Array.isArray(route.query.nodeId) ? route.query.nodeId[0] : route.query.nodeId;
  return raw ? String(raw) : null;
});

function isoDate(value: string, endOfDay = false): string {
  return new Date(`${value}T${endOfDay ? '23:59:59' : '00:00:00'}+08:00`).toISOString();
}

/** 当前在抽屉里编辑的节点 */
const editingTask = ref<PlanTask | null>(null);

function openNode(task: PlanTask): void {
  editingTask.value = task;
}

async function saveFromDrawer(
  task: PlanTask,
  payload: { start: string; end: string; status: string | null },
): Promise<void> {
  await saveSchedule(task, payload);
  editingTask.value = null;
}

async function saveSchedule(
  task: PlanTask,
  payload: { start: string; end: string; status: string | null },
): Promise<void> {
  const wasUnscheduled = !task.planStart || !task.planEnd;
  let rescheduleExtra: Record<string, string> = {};
  if (nodeNeedsRescheduleReason(task, payload.end)) {
    const answer = await reschedule.ask({ title: task.name, fromDay: dayOf(task.planEnd), toDay: payload.end });
    if (!answer) return;
    rescheduleExtra = rescheduleFields(answer, 'single');
  }
  savingNodeId.value = task.id;
  try {
    await eqsApi.updatePlanNode(props.project.id, task.id, {
      plannedStartAt: isoDate(payload.start),
      plannedEndAt: isoDate(payload.end, true),
      ...rescheduleExtra,
      // 留空表示只动日期，不能把「没选状态」误当成一次状态变更
      ...(payload.status ? { status: payload.status } : {}),
    });
    notify(
      'success',
      wasUnscheduled ? '排期已补齐' : '计划已更新',
      `“${task.name}”${payload.status ? '的日期与状态' : '的计划日期'}已保存，变更已记录审计。`,
    );
    await load({ silent: true });
  } catch (caught) {
    notify('error', '保存失败', errorMessage(caught));
  } finally {
    savingNodeId.value = null;
  }
}

/**
 * 拖拽落下即写入。
 *
 * 手势本身就是确认，再弹一次框比点开表单还慢。写完之后再问依赖：
 * 有后置需要跟着动时给一次确认，没有就安静收工。
 */
async function applyDrag(task: PlanTask, change: { start: string; end: string }): Promise<void> {
  // 已排期、还开着的活动拖动了完成日 → 先问原因（0926）；不写就把条子放回原处
  let rescheduleExtra: Record<string, string> = {};
  if (nodeNeedsRescheduleReason(task, change.end)) {
    const answer = await reschedule.ask({ title: task.name, fromDay: dayOf(task.planEnd), toDay: change.end });
    if (!answer) {
      await load({ silent: true });
      return;
    }
    rescheduleExtra = rescheduleFields(answer, 'drag');
  }
  savingNodeId.value = task.id;
  // 先把改前的日期留住：拖拽没有确认框，鼠标一滑就是一条审计，
  // 必须给一条明确的退路，否则「挪错一格」只能靠人自己记住原来是哪天。
  const before = [{
    nodeId: Number(task.id),
    plannedStartAt: task.planStart ? isoDate(task.planStart.slice(0, 10)) : null,
    plannedEndAt: task.planEnd ? isoDate(task.planEnd.slice(0, 10), true) : null,
  }];
  try {
    await eqsApi.updatePlanNode(props.project.id, task.id, {
      plannedStartAt: isoDate(change.start),
      plannedEndAt: isoDate(change.end, true),
      ...rescheduleExtra,
    });
    undoable.value = { label: `“${task.name}”改为 ${change.start} → ${change.end}`, updates: before };
    notify('success', '排期已调整', '如果拖错了，用下方的「撤销」可以还原。');
    await load({ silent: true });
    await offerCascade(task, change.end);
  } catch (caught) {
    notify('error', '排期保存失败', errorMessage(caught));
    await load({ silent: true });   // 失败就回到库里的真实值，不留一个假的位置
  } finally {
    savingNodeId.value = null;
  }
}

/**
 * 最近一次可撤销的改动。
 *
 * 存的是「改之前的值」，撤销就是把它们原样写回去——不是反向计算位移，
 * 那样遇到级联顺延会算不准。级联一起接受时，后置的原值也会并进来。
 */
const undoable = ref<{ label: string; updates: Array<Record<string, unknown>> } | null>(null);
const undoing = ref(false);

async function undoLast(): Promise<void> {
  const snapshot = undoable.value;
  if (!snapshot) return;
  undoing.value = true;
  try {
    // 撤销也是一次改期：原因自动带上，来源 undo，延期统计里分得清
    await eqsApi.batchUpdatePlanNodes(props.project.id, snapshot.updates.map((update) => ({
      ...update,
      ...rescheduleFields({ category: '其他', reason: '撤销上一次改动，还原到改动前的日期' }, 'undo'),
    })));
    notify('success', '已撤销', '排期已还原到改动前。撤销本身也会记入审计。');
    undoable.value = null;
    await load({ silent: true });
  } catch (caught) {
    notify('error', '撤销失败', errorMessage(caught));
  } finally {
    undoing.value = false;
  }
}

/** 问后端：这一改有没有后置需要顺延、有没有撞到前置 */
async function offerCascade(task: PlanTask, end: string): Promise<void> {
  try {
    const result = await eqsApi.previewCascade(props.project.id, [
      { nodeId: Number(task.id), end },
    ]);
    const conflicts = (result.conflicts ?? []) as string[];
    if (conflicts.length) {
      notify('error', '与前后置依赖冲突', conflicts.slice(0, 3).join('；'));
    }
    const suggestions = (result.suggestions ?? []) as Array<{
      nodeId: number; name: string;
      currentStart: string | null; currentEnd: string | null;
      nextStart: string; nextEnd: string | null;
    }>;
    if (!suggestions.length) return;

    const preview = suggestions.slice(0, 5)
      .map((item) => `${item.name} → ${item.nextStart}`).join('\n');
    const more = suggestions.length > 5 ? `\n…共 ${suggestions.length} 条` : '';
    const question = `有 ${suggestions.length} 个后置任务需要顺延：\n`
      + `${preview}${more}\n\n要一起调整吗？`;
    if (!await confirmAction({ title: question })) {
      return;
    }
    // 后置顺延的原因自动带上：上游节点延后，随「谁」顺延；来源 cascade
    await eqsApi.batchUpdatePlanNodes(props.project.id, suggestions.map((item) => ({
      nodeId: item.nodeId,
      plannedStartAt: isoDate(item.nextStart),
      ...(item.nextEnd
        ? {
          plannedEndAt: isoDate(item.nextEnd, true),
          ...rescheduleFields({ category: '上游节点延后', reason: `随「${task.name}」改期顺延` }, 'cascade'),
        }
        : {}),
    })));
    // 顺延的后置也要能一起撤回来：拖一下带动十几条，撤销只还原一条等于没用
    if (undoable.value) {
      undoable.value = {
        label: `${undoable.value.label}，并顺延 ${suggestions.length} 个后置`,
        updates: [
          ...undoable.value.updates,
          ...suggestions.map((item) => ({
            nodeId: item.nodeId,
            plannedStartAt: item.currentStart ? isoDate(item.currentStart) : null,
            plannedEndAt: item.currentEnd ? isoDate(item.currentEnd, true) : null,
          })),
        ],
      };
    }
    notify('success', `已顺延 ${suggestions.length} 个后置任务`, '变更已记入审计。');
    await load({ silent: true });
  } catch (caught) {
    notify('error', '依赖顺延失败', errorMessage(caught));
  }
}

const dependencies = ref<PlanDependency[]>([]);

async function loadDependencies(): Promise<void> {
  try {
    const payload = await eqsApi.planDependencies(props.project.id);
    dependencies.value = (payload.dependencies ?? []) as unknown as PlanDependency[];
  } catch {
    // 依赖取不到只是画不出连线，甘特本身还得能看
    dependencies.value = [];
  }
}

async function linkDependency(payload: { predecessorId: number; successorId: number }): Promise<void> {
  try {
    const result = await eqsApi.createPlanDependency(props.project.id, { ...payload, lagDays: 0 });
    dependencies.value = (result.dependencies ?? []) as unknown as PlanDependency[];
    const created = dependencies.value.find((item) =>
      item.predecessorId === payload.predecessorId && item.successorId === payload.successorId);
    if (created?.violated) {
      // 建完就冲突是常事（本来就排错了才要建依赖），说清楚而不是拦着不让建
      notify('error', '依赖已建立，但当前日期违反它',
        `“${created.successorName}”最早只能从 ${created.earliestStart ?? '—'} 开始。`);
    } else {
      notify('success', '前置已设置', '前置推迟时，这条会收到顺延建议。');
    }
  } catch (caught) {
    // 后端会挡住成环和重复，把原话透出来
    notify('error', '前置设置失败', errorMessage(caught));
  }
}

async function unlinkDependency(dependency: PlanDependency): Promise<void> {
  try {
    const result = await eqsApi.removePlanDependency(props.project.id, dependency.id);
    dependencies.value = (result.dependencies ?? []) as unknown as PlanDependency[];
    notify('success', '依赖已解除', `${dependency.predecessorName} → ${dependency.successorName}`);
  } catch (caught) {
    notify('error', '解除失败', errorMessage(caught));
  }
}

/**
 * silent=true：保存/拖拽之后的原位刷新。
 *
 * 试用反馈 0819：「此页面修改页面就会跳转回原始状态」——改一条日期，
 * 行范围跳回「阶段概览」、展开的阶段收起、滚动位置回顶。
 * 根因是每次保存后 load() 都把 loading 置 true，模板切到 PageState，
 * **整个 GanttBoard 被卸载重建**，内部的 taskScope/expandedPhases/滚动全没了。
 * 静默刷新只换数据不动 loading，组件不卸载，改完一条接着改下一条。
 * 首次加载和手动重试仍走完整 loading 态——那时本来就没有视图状态可保。
 */
async function load(options: { silent?: boolean } = {}): Promise<void> {
  if (!options.silent) loading.value = true;
  error.value = '';
  try {
    const payload = await eqsApi.projectPlan(props.project.id);
    if (!payload || !Array.isArray(payload.tasks)) throw new Error('项目计划响应缺少 tasks 数组');
    data.value = payload;
    focusRequestedTask();
    await loadDependencies();
  } catch (caught) {
    data.value = null;
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
}

function focusRequestedTask(): void {
  if (!focusTaskId.value || !data.value) return;
  const task = data.value.tasks.find((item) => String(item.id) === focusTaskId.value);
  const phase = task ? phaseFor(task) : null;
  if (phase) phaseFilter.value = phase;
}

watch(focusTaskId, focusRequestedTask);
// 从 Gate 导航进来时自动过滤到该 Gate 所属阶段
watch([gatePhase, gateCode], () => {
  if (gatePhase.value) phaseFilter.value = taskKey(gatePhase.value);
  else if (!gateCode.value) phaseFilter.value = 'all';
}, { immediate: true });
onMounted(load);
</script>

<template>
  <header class="subpage-context">
    <p><span>SCHEDULE / GANTT</span>在时间轴内查看项目节奏，并直接补齐缺失的计划开始与完成日期。</p>
    <small v-if="data">数据截至 {{ formatDate(data.asOf) }}</small>
  </header>
  <PageState v-if="loading" state="loading" title="正在读取项目甘特计划" message="加载阶段、任务和计划日期。" />
  <PageState v-else-if="error" state="error" title="项目甘特不可用" message="不会用静态数据或推算日期替代。" :detail="error" @retry="load" />
  <template v-else-if="data">
    <!--
      改不了的时候要说清为什么（0914 第 12 条）。
      按钮灰掉却不给理由，人只会以为系统坏了——0829 的教训:
      「藏起来又挡路是最坏的组合」。
    -->
    <p v-if="planReadOnly" class="plan-readonly">{{ planReadOnly }}</p>
    <section v-if="gateCode" class="gate-focus" :aria-label="`${gateCode} 就绪度`">
      <div class="gate-focus-head">
        <span>{{ gateCode }}</span>
        <b>{{ gateReadiness?.phaseName ?? '未找到对应阶段' }}</b>
        <small v-if="gateStage">按 {{ gateStage }} 阶段统计</small>
        <small v-if="gateMilestone?.planDate">计划 {{ formatDate(gateMilestone.planDate) }}</small>
        <small v-else>计划日期未维护</small>
      </div>
      <dl v-if="gateReadiness">
        <div>
          <dt>关键交付物</dt>
          <dd :class="{ risk: gateReadiness.criticalDone < gateReadiness.criticalTotal }">
            {{ gateReadiness.criticalDone }} / {{ gateReadiness.criticalTotal }}
          </dd>
        </div>
        <div>
          <dt>已完成待审批</dt>
          <dd :class="{ warning: gateReadiness.awaitingApproval > 0 }">{{ gateReadiness.awaitingApproval }}</dd>
        </div>
        <div>
          <dt>阶段任务</dt>
          <dd>{{ gateReadiness.total }}</dd>
        </div>
        <div>
          <dt>已逾期</dt>
          <dd :class="{ risk: gateReadiness.overdue > 0 }">{{ gateReadiness.overdue }}</dd>
        </div>
        <div>
          <dt>未排期</dt>
          <dd :class="{ warning: gateReadiness.unscheduled > 0 }">{{ gateReadiness.unscheduled }}</dd>
        </div>
      </dl>
      <p v-else class="gate-focus-empty">
        这道 Gate 在本项目里没有对应的阶段节点，无法给出就绪度。
      </p>
      <p class="gate-focus-note">
        以上是事实统计，不代表 Gate 能否通过——放行与否由评审会决定。
      </p>
    </section>

    <!-- 隐藏了多少条要写出来：列表少数据却不吭声是这套系统吃过亏的老毛病 -->
    <p v-if="notApplicableCount" class="plan-hidden-note">
      <template v-if="showNotApplicable">
        正在显示 {{ notApplicableCount }} 个在「阶段工作内容」里标为<b>不适用</b>的活动。
      </template>
      <template v-else>
        已隐藏 {{ notApplicableCount }} 个在「阶段工作内容」里标为<b>不适用</b>的活动。
      </template>
      <button class="inline-button" type="button" @click="showNotApplicable = !showNotApplicable">
        {{ showNotApplicable ? '重新隐藏' : '也显示它们' }}
      </button>
      <RouterLink :to="`/projects/${project.id}/activities`">去阶段工作内容调整 →</RouterLink>
    </p>

    <section class="plan-filter" aria-label="甘特阶段筛选">
      <label>
        <span>阶段</span>
        <select v-model="phaseFilter">
          <option value="all">全部阶段</option>
          <option v-for="phase in phaseOptions" :key="phase.value" :value="phase.value">{{ phase.label }}</option>
        </select>
      </label>
      <button
        v-if="mineOnly || mineCount"
        type="button"
        class="inline-button mine-toggle"
        :class="{ 'is-on': mineOnly }"
        @click="mineOnly = !mineOnly"
      >只看我负责的<b>{{ mineCount }}</b></button>
      <div class="plan-filter-actions">
        <strong>{{ visibleTasks.length }} <small>/ {{ allTasks.length }} 节点</small></strong>
        <ExportDataButton
          :columns="planExportColumns"
          :rows="exportRows"
          :filename="exportFilename"
          label="导出当前计划"
          compact
        />
        <RouterLink :to="{ name: 'project-activities', params: { projectId: String(project.id) } }">进入阶段工作 →</RouterLink>
      </div>
    </section>

    <p v-if="mineOnly && !visibleTasks.some((task) => task.isMine)" class="plan-hidden-note">
      当前筛选下<b>你名下没有节点</b>。{{ gateStage ? `这道门在 ${gateStage} 阶段，` : '' }}这个项目一共 {{ mineCount }} 个节点归你。
      <button class="inline-button" type="button" @click="mineOnly = false">看全部</button>
    </p>

    <p v-if="undoable" class="undo-strip">
      <span>{{ undoable.label }}</span>
      <button type="button" :disabled="undoing" @click="undoLast">
        {{ undoing ? '撤销中…' : '撤销' }}
      </button>
      <button class="undo-dismiss" type="button" @click="undoable = null">不用了</button>
    </p>

    <SchedulePlanner
      :project-id="project.id"
      :can-write="canWrite"
      @applied="() => load({ silent: true })"
    />

    <GanttBoard
      :tasks="visibleTasks"
      :range-tasks="data.tasks"
      :as-of="data.asOf"
      :can-write="canWrite"
      :saving-task-id="savingNodeId"
      :focus-task-id="focusTaskId"
      :preferred-scope="gateCode ? 'key' : null"
      :dependencies="dependencies"
      @open="openNode"
      @drag="applyDrag"
      @link="linkDependency"
      @unlink="unlinkDependency"
    />

    <PlanNodeDrawer
      :task="editingTask"
      :project-id="project.id"
      :tasks="allTasks"
      :can-write="canWrite"
      :saving="savingNodeId !== null"
      @close="editingTask = null"
      @save="saveFromDrawer"
      @dependencies-changed="loadDependencies"
    />

    <NodeRescheduleDialog
      v-if="reschedule.pending.value"
      :title="reschedule.pending.value.title"
      :from-day="reschedule.pending.value.fromDay"
      :to-day="reschedule.pending.value.toDay"
      :count="reschedule.pending.value.count"
      @confirm="reschedule.settle($event)"
      @cancel="reschedule.settle(null)"
    />
  </template>
</template>

<style scoped>
.plan-readonly {
  margin: 0 0 14px; padding: 10px 14px;
  border: 1px solid var(--line); border-left: 3px solid var(--blue);
  border-radius: var(--radius-s); background: var(--white);
  color: var(--muted); font-size: 12px; line-height: 1.8;
}

/* 拖拽没有确认框，必须给一条看得见的退路 */
.undo-strip {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin: 14px 0 0;
  padding: 9px 12px;
  border: 1px solid var(--blue);
  border-left: 3px solid var(--blue);
  background: #EEF4FA;
  font-size: 13px;
}

.undo-strip > span { flex: 1 1 auto; min-width: 0; }

.undo-strip button {
  padding: 4px 12px;
  border: 1px solid var(--blue);
  background: var(--white);
  color: var(--blue);
  font: 700 12px var(--body);
  cursor: pointer;
}

.undo-strip button:hover:not(:disabled) { background: var(--blue); color: white; }
.undo-strip button:disabled { opacity: .5; cursor: wait; }
.undo-strip .undo-dismiss { border-color: var(--line); color: var(--muted); font-weight: 400; }

.gate-focus {
  margin-top: 18px;
  padding: 14px 18px;
  border: 1px solid var(--line);
  border-left: 3px solid var(--blue);
  background: var(--white);
}

.gate-focus-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.gate-focus-head span {
  color: var(--blue);
  font: 800 13px var(--display);
  letter-spacing: .1em;
}

.gate-focus-head b { font-size: 15px; }
.gate-focus-head small { color: var(--muted); font-size: 12px; }

.gate-focus dl {
  display: flex;
  gap: 28px;
  flex-wrap: wrap;
  margin: 12px 0 0;
}

.gate-focus dt { color: var(--muted); font-size: 12px; }
.gate-focus dd { margin: 2px 0 0; font: 700 21px var(--display); }
.gate-focus dd.risk { color: var(--risk); }
.gate-focus dd.warning { color: var(--warning); }
.gate-focus-empty { margin: 10px 0 0; color: var(--risk); font-size: 13px; }

.gate-focus-note {
  margin: 10px 0 0;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.7;
}

.plan-filter {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: end;
  margin-top: 18px;
  padding: 14px 16px;
  border: 1px solid var(--line);
  border-radius: var(--radius-s);
  color: var(--ink);
  background: var(--white);
}

.plan-filter label {
  min-width: min(320px, 70vw);
  display: grid;
  gap: 6px;
}

.plan-filter label span,
.plan-filter strong small {
  color: var(--muted);
  font-size: 12px;
}

.plan-filter select {
  min-height: 42px;
  padding: 0 10px;
  border: 1px solid var(--line);
  border-radius: var(--radius-s);
  color: var(--ink);
  background: var(--white);
  font-size: 14px;
}
.plan-filter-actions { display: flex; align-items: center; gap: 18px; }
.plan-filter strong { font: 700 24px var(--display); }
.plan-filter a {
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  padding: 0 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius-s);
  color: var(--ink);
  background: var(--white);
  text-decoration: none;
  font-size: 13px;
  font-weight: 700;
}
.plan-filter a:hover { border-color: var(--blue); color: var(--blue); }

@media (max-width: 700px) {
  .plan-filter { align-items: stretch; flex-direction: column; }
  .plan-filter label { min-width: 0; }
  .plan-filter-actions { justify-content: space-between; }
}
.plan-hidden-note {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  margin: 0 0 10px; padding: 8px 12px; border-left: 3px solid var(--blue);
  background: #EEF4FA; font-size: 12px; line-height: 1.7;
}
.plan-hidden-note a {
  border: 0; background: transparent; color: var(--blue); font: inherit;
  text-decoration: underline; cursor: pointer;
}
/* 「只看我负责的」：亮起来时是实心蓝，一眼看出正在过滤 */
.mine-toggle b { margin-left: 5px; color: var(--muted); font-size: 11px; }
.mine-toggle.is-on {
  border-color: var(--blue); background: var(--blue); color: var(--white);
}
.mine-toggle.is-on b { color: rgba(255, 255, 255, .82); }
</style>
